// Web port of app/src/contexts/SessionContext.js — keep in sync with it.
//
// The mobile context fetches the signed-in user's account once after auth, then
// leaves flags describing two off-happy-path conditions Navigation must remedy:
//   * the account fetch failed (API error / timeout), and
//   * the account is missing or its accepted terms are stale.
// This web port keeps ONLY that read behaviour. Every mobile write method
// (createHaloAccount / updateHaloAccount / acceptTerms / invite handling) is
// dropped: this client is strictly read-only, so account creation and terms
// acceptance live on the Halo mobile app, and the invites list only ever fed
// those writes. What remains is the account probe and the two derived flags the
// SessionGate reads to decide what to render.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ACCOUNT } from 'darwin_core/api/jsonapi.js';
import { CURRENT_TERMS_VERSION } from 'darwin_core/utils/consts.js';

import { useAuth } from '../auth/AuthenticationContext.jsx';
import { useJsonApi } from '../api/JsonApiProvider.jsx';
import logger from '../shims/Logger.js';

export const SessionContext = createContext(null);

// Consumer hook. Throws outside a provider so a mis-wired tree fails loudly at
// mount rather than silently reading a default, mirroring useAuth.
export function useSession() {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
}

export function SessionProvider({ children }) {
  const { authenticated, currentAuthUser } = useAuth();
  const { jsonApi } = useJsonApi();

  // The account id is the Cognito subject: the REST client derives the account
  // from the token it sends, so this only gates whether we probe at all.
  const cognitoId = currentAuthUser?.sub;

  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  // false when healthy; a string (the error title) or `true` when the fetch
  // failed for a reason other than a missing account.
  const [fetchError, setFetchError] = useState(false);
  const [hasAccount, setHasAccount] = useState(false);
  const [termsVersion, setTermsVersion] = useState(null);

  const abortControllerRef = useRef();

  const fetchAccount = useCallback(
    async function fetchAccount(refresh = false) {
      setLoading(true);

      if (jsonApi && cognitoId && (!hasAccount || refresh)) {
        try {
          const abortController = new AbortController();

          // Abort any probe still in flight before starting a new one so a slow
          // earlier request can never resolve over a newer one's result.
          if (abortControllerRef.current) {
            abortControllerRef.current.abort();
          }

          abortControllerRef.current = abortController;

          logger.log('Fetching account');
          // The account id is determined from the token sent with the request.
          const account = await jsonApi.find(ACCOUNT, cognitoId, {
            opts: {
              signal: abortController.signal,
              timeout: 15000,
            },
          });
          logger.log('Got account');

          setHasAccount(true);
          setTermsVersion(account?.data?.TermsVersion);
          setFetchError(false);
        } catch (errors) {
          if (!errors.cancelled) {
            // devour surfaces JSON:API errors as an array; a thrown Error comes
            // through as-is. Normalise to the first entry when there is one.
            const err = errors[0] ? errors[0] : errors;

            if (err?.title === 'Not found') {
              // A missing account is an expected off-path state (the user must
              // finish setup on mobile), NOT an error to surface.
              logger.log('No account found for the signed-in user');
              setHasAccount(false);
            } else {
              // Log the title only — never the raw error, which could carry
              // request material.
              logger.error('Error when getting user account', err?.title);
              setFetchError(err?.title || true);
            }
          }
        } finally {
          setLoaded(true);
        }
      }

      setLoading(false);
    },
    [cognitoId, hasAccount, jsonApi],
  );

  // Probe once on sign-in; reset every flag on sign-out so a shared browser
  // never shows the previous user's session state.
  useEffect(() => {
    if (authenticated) {
      fetchAccount();
    } else {
      // Abort any probe still in flight from the previous session. Otherwise it
      // could resolve AFTER sign-out and set hasAccount=true / a stale
      // termsVersion; the next user's fetchAccount would then short-circuit on the
      // `(!hasAccount || refresh)` guard and inherit the previous user's terms
      // state (SECURITY — shared browser). A cancelled probe is ignored by the
      // catch's `errors.cancelled` check, leaving hasAccount=false so the next
      // user is probed fresh.
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      setLoaded(false);
      setFetchError(false);
      setHasAccount(false);
      setTermsVersion(null);
    }
  }, [fetchAccount, authenticated]);

  // Abort an in-flight probe on unmount so its resolution can't touch unmounted
  // state.
  useEffect(function tidyUp() {
    return () => {
      if (abortControllerRef.current) {
        logger.log('Cleaning up account fetch');
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  // Same predicates as mobile, minus the write-only bits (no creatingAccount
  // gate, since the web client never creates an account):
  //  * setup/terms notice when the account is missing or its terms are stale;
  //  * session error when the fetch failed for any other reason.
  const shouldShowTermsAndConditions =
    authenticated &&
    loaded &&
    !fetchError &&
    (!hasAccount || termsVersion !== CURRENT_TERMS_VERSION);

  const shouldShowSessionError = authenticated && loaded && !!fetchError;

  const value = useMemo(
    () => ({
      loading,
      loaded,
      hasAccount,
      termsVersion,
      fetchError,
      fetchAccount,
      shouldShowTermsAndConditions,
      shouldShowSessionError,
    }),
    [
      loading,
      loaded,
      hasAccount,
      termsVersion,
      fetchError,
      fetchAccount,
      shouldShowTermsAndConditions,
      shouldShowSessionError,
    ],
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}
